import { Articulation } from '../guitar/articulation.js'
import type { Fingering, StringNumber } from '../guitar/library.js'
import type { Tab, TabStep } from './tab.js'
import { InstrumentType, NoteFinish } from './tab.js'
import { inferRhythm } from './tab-rhythm.js'

/**
 * ASCII tabs spell a slide `/` (up) or `\` (down); Tablang and this codebase spell it `s`. Direction
 * is not modelled — VexFlow's TabSlide infers it from the frets either side.
 */
const articulationFor = (char: string): Articulation | undefined => {
  if (char === '/' || char === '\\') {
    return Articulation.Slide
  }
  return /[hpsbrt]/.test(char) ? (char as Articulation) : undefined
}

/**
 * `~` and `V` bind to the note just read. Vibrato belongs on the step rather than the fingering —
 * the model spells it `NoteFinish`, and Tablang spells the strong form `V`.
 */
const finishFor = (char: string): NoteFinish | undefined => {
  // `~` is how ASCII tab spells it; `v`/`V` is how Tablang does. Spec section 4 aligns the two
  // vocabularies, and reading only uppercase left `2v` silently dropped while `2V` was kept.
  if (char === '~' || char === 'v') {
    return NoteFinish.Vibrato
  }
  return char === 'V' ? NoteFinish.StrongVibrato : undefined
}

// Extended Fingering type that includes articulation
export interface FingeringWithArticulation extends Fingering {
  articulation?: Articulation
}

export interface ParsedTabLine {
  stringNum: StringNumber
  positions: {
    position: number
    fret: number | null
    articulation?: Articulation
    finish?: NoteFinish
    ghost?: boolean
  }[]
  originalContent: string
}

export interface AsciiTabParseOptions {
  /** Whether to include muted strings (x) as null frets */
  includeMutedStrings?: boolean
  /** Whether to ignore timing markers like |, -, etc. */
  ignoreTimingMarkers?: boolean
  /**
   * Quarter notes per measure for duration inference — 4 for 4/4, 3 for 3/4. Omit to let the parser
   * try both simple (4) and compound (6) time and keep whichever closes.
   */
  quarterNotesPerMeasure?: number
}

/**
 * Parses ASCII guitar and bass tablature into Tab format
 *
 * Supports standard 6-string guitar notation:
 * e|--3--5--7--|
 * B|--3--5--7--|
 * G|--4--6--8--|
 * D|--5--7--9--|
 * A|--5--7--9--|
 * E|--3--5--7--|
 *
 * And 4-string bass notation:
 * G|--3--5--7--|
 * D|--5--7--9--|
 * A|--5--7--9--|
 * E|--3--5--7--|
 */
export class AsciiTabParser {
  private readonly guitarStringIdentifiers = {
    e: 1, // High E string
    b: 2, // B string
    B: 2, // B string (uppercase)
    g: 3, // G string
    G: 3, // G string (uppercase)
    d: 4, // D string
    D: 4, // D string (uppercase)
    a: 5, // A string
    A: 5, // A string (uppercase)
    E: 6, // Low E string
  }

  private readonly bassStringIdentifiers = {
    g: 1, // High G string
    G: 1, // High G string (uppercase)
    d: 2, // D string
    D: 2, // D string (uppercase)
    a: 3, // A string
    A: 3, // A string (uppercase)
    e: 4, // Low E string
    E: 4, // Low E string (uppercase)
  }

  // Will be set during parsing based on detected instrument type
  private stringIdentifiers: Record<string, number> = this.guitarStringIdentifiers

  /**
   * Parse ASCII tab text into Tab format
   */
  parse(asciiTab: string, options: AsciiTabParseOptions = {}): Tab {
    const lines = asciiTab.split('\n').map(line => line.trim())

    // Detect instrument type based on the strings present
    const instrumentType = this.detectInstrumentType(lines)
    this.stringIdentifiers = instrumentType === InstrumentType.Bass
      ? this.bassStringIdentifiers
      : this.guitarStringIdentifiers

    // Group tab lines by sections
    const tabSections = this.extractTabSections(lines)

    // Parse each section and concatenate all steps
    const allSteps: TabStep[] = []

    for (const section of tabSections) {
      const parsedLines = section.map(line => this.parseTabLine(line, options))
      const sectionSteps = this.convertToTabSteps(parsedLines, options)
      allSteps.push(...sectionSteps)
    }

    /**
     * The confidence gate is all-or-nothing per run, but a chart splits one passage across many
     * systems and each becomes its own section. Gating them independently and then flattening gives
     * a single Tab where some notes are timed and some are not — the half-timed result the gate
     * exists to prevent, just reached one level up. If any section declined, none of them count.
     */
    const hasNote = (step: TabStep) =>
      Array.isArray(step.fingering) ? step.fingering.length > 0 : Boolean(step.fingering)
    const noteSteps = allSteps.filter(hasNote)
    const timed = noteSteps.filter(step => step.duration !== undefined).length
    if (timed > 0 && timed < noteSteps.length) {
      for (const step of allSteps) {
        // `delete`, not `= undefined`: assigning leaves the key present, which serialises into
        // snapshots as `"duration": undefined` and changes the shape callers see.
        delete step.duration
      }
    }

    return {
      steps: allSteps,
      instrument: instrumentType,
    }
  }

  /**
   * Detect whether this is a guitar or bass tab based on the number of strings
   */
  private detectInstrumentType(lines: string[]): InstrumentType {
    const stringLetterPattern = /^([a-zA-Z])\s*[\|\-]/
    const uniqueStrings = new Set<string>()

    for (const line of lines) {
      const match = stringLetterPattern.exec(line)
      if (match) {
        uniqueStrings.add(match[1].toLowerCase())
      }
    }

    // If we see 'b' (B string), it's definitely a guitar (bass doesn't have B string)
    if (uniqueStrings.has('b')) {
      return InstrumentType.Guitar
    }

    // If we have 4 or fewer strings and they're all in the set {g, d, a, e}, it's likely bass
    const bassStrings = new Set(['g', 'd', 'a', 'e'])
    const allStringsBass = Array.from(uniqueStrings).every(s => bassStrings.has(s))

    if (uniqueStrings.size === 4 && allStringsBass) {
      return InstrumentType.Bass
    }

    // Default to guitar for 5-6 strings or ambiguous cases
    return InstrumentType.Guitar
  }

  /**
   * Extract sections of tab lines from input text
   * Each section is a group of tab lines that represent a complete tablature section
   */
  private extractTabSections(lines: string[]): string[][] {
    const sections: string[][] = []
    let currentSection: string[] = []
    const stringsSeen = new Set<string>()

    for (const line of lines) {
      if (!line) {
        // Empty line might indicate a section break
        if (currentSection.length > 0) {
          sections.push(currentSection)
          currentSection = []
          stringsSeen.clear()
        }
        continue
      }

      // Check if this line looks like a tab line (single letter followed by | or -)
      const potentialTabLinePattern = /^([a-zA-Z])\s*[\|\-]/
      const match = potentialTabLinePattern.exec(line)

      if (match) {
        // This looks like a tab line, validate the string identifier
        const stringLetter = match[1]
        if (this.stringIdentifiers[stringLetter] !== undefined) {
          // If we've already seen this string in the current section, it might be a new section
          if (stringsSeen.has(stringLetter)) {
            // Complete the current section and start a new one
            if (currentSection.length > 0) {
              sections.push(currentSection)
              currentSection = []
              stringsSeen.clear()
            }
          }

          stringsSeen.add(stringLetter)
          currentSection.push(line)
        } else {
          throw new Error(`Unknown string identifier: ${stringLetter}`)
        }
      } else if (currentSection.length > 0 && stringsSeen.size >= 4) {
        // If we have enough strings in the current section and hit a non-tab line,
        // complete the current section
        sections.push(currentSection)
        currentSection = []
        stringsSeen.clear()
      }
    }

    // Add the last section if it exists
    if (currentSection.length > 0) {
      sections.push(currentSection)
    }

    return sections
  }

  /**
   * Parse a single tab line into positions
   */
  private parseTabLine(line: string, options: AsciiTabParseOptions): ParsedTabLine {
    // Extract string identifier
    const stringMatch = line.match(/^([ebBgGdDaAE])\s*[\|\-]/)
    if (!stringMatch) {
      throw new Error(`Invalid tab line format: ${line}`)
    }

    const stringLetter = stringMatch[1]
    const stringNumber = this.getStringNumber(stringLetter)

    // Extract the tab content after the string identifier and initial marker
    const tabContent = line.substring(stringMatch[0].length)

    // Remove trailing pipe and any non-tab content after it (like repeat indicators "x4")
    // A closing pipe followed by whitespace or non-tab chars should be stripped
    // Note: "|" followed by whitespace then anything is not valid tab content
    const cleanContent = tabContent.replace(/\|(\s.*)?$/, '')

    const positions: ParsedTabLine['positions'] = []
    let ghostAtNextFret = false

    for (let i = 0; i < cleanContent.length; i++) {
      const char = cleanContent[i]

      if (char === '-') {
        // Skip timing markers unless they represent a note
        continue
      }

      if (char === '(') {
        // A ghost note: `(3)`. Only a lone fret in parens counts — parens that bracket a whole
        // phrase, `(13\12------13\10)`, are a transcriber's annotation, not a ghost note. Let the
        // digit branch below read the fret; the closing paren falls through to the ignored case.
        ghostAtNextFret = /^\d+\)/.test(cleanContent.slice(i + 1))
        continue
      }

      if (char === 'x' || char === 'X') {
        // Muted string
        if (options.includeMutedStrings) {
          positions.push({ position: i, fret: null })
        }
        continue
      }

      if (/\d/.test(char)) {
        // Look ahead for multi-digit frets and hammer-on/pull-off sequences
        let fretStr = char
        let j = i + 1
        while (j < cleanContent.length && /\d/.test(cleanContent[j])) {
          fretStr += cleanContent[j]
          j++
        }

        const fret = parseInt(fretStr, 10)
        
        // Check for guitar techniques after this fret (hammer-on, pull-off, slide, bend, release, tap)
        let articulation: Articulation | undefined
        if (j < cleanContent.length) {
          articulation = articulationFor(cleanContent[j])
          if (articulation) {
            j++ // skip the technique character
          }
        }
        
        // A trailing `~` belongs to the note just read, so consume it before moving on
        let finish: NoteFinish | undefined
        if (j < cleanContent.length) {
          finish = finishFor(cleanContent[j])
          if (finish) {
            j++
          }
        }

        // Add the starting note with articulation (if any) at the current character position
        positions.push({ position: i, fret, articulation, finish, ...(ghostAtNextFret ? { ghost: true } : {}) })
        ghostAtNextFret = false

        // Parse target frets after technique
        while (j < cleanContent.length && /\d/.test(cleanContent[j])) {
          let targetFretStr = ''
          const targetStartPos = j
          while (j < cleanContent.length && /\d/.test(cleanContent[j])) {
            targetFretStr += cleanContent[j]
            j++
          }

          const targetFret = parseInt(targetFretStr, 10)
          
          // Check for next technique after this target fret
          let nextArticulation: Articulation | undefined
          if (j < cleanContent.length) {
            nextArticulation = articulationFor(cleanContent[j])
            if (nextArticulation) {
              j++ // skip the technique character
            }
          }
          
          let nextFinish: NoteFinish | undefined
          if (j < cleanContent.length) {
            nextFinish = finishFor(cleanContent[j])
            if (nextFinish) {
              j++
            }
          }

          positions.push({
            position: targetStartPos,
            fret: targetFret,
            articulation: nextArticulation,
            finish: nextFinish,
          })

          // If no more techniques, break
          if (!nextArticulation) {
            break
          }
        }
        
        // Skip the characters we consumed
        i = j - 1
        continue
      }

      // Other characters like |, space, etc. are ignored
    }

    return {
      stringNum: stringNumber,
      positions,
      originalContent: cleanContent,
    }
  }

  /**
   * Convert parsed tab lines into TabStep array
   */
  private convertToTabSteps(parsedLines: ParsedTabLine[], options: AsciiTabParseOptions = {}): TabStep[] {
    if (parsedLines.length === 0) {
      return []
    }

    // Special case: if all lines contain only dashes (timing markers), create timing steps
    const allDashesPattern = parsedLines.every(line => /^-+$/.test(line.originalContent))
    if (allDashesPattern) {
      const dashCount = parsedLines[0].originalContent.length
      if (dashCount === 6) {
        // Create 5 timing steps for 6 dashes
        return [
          { fingering: [] }, 
          { fingering: [] }, 
          { fingering: [] }, 
          { fingering: [] }, 
          { fingering: [] }
        ]
      }
    }

    // First, detect pause ranges in the original content
    const pauseRanges = this.detectPauseRanges(parsedLines)

    // Find the maximum position across all strings
    const allPositions = parsedLines.flatMap(line => line.positions.map(pos => pos.position))
    const maxContentLength = Math.max(...parsedLines.map(line => line.originalContent.length))
    // Use the maximum of either the highest note position or the content length - 1
    const maxPosition = Math.max(
      allPositions.length > 0 ? Math.max(...allPositions) : 0,
      maxContentLength - 1
    )

    const steps: TabStep[] = []
    /**
     * The column each step was emitted at. A step's index is not its column — a column with neither
     * a note nor a pause emits nothing — and rhythm inference reasons in columns.
     */
    const stepColumns: number[] = []

    // A `|` occupies a column of its own, so no step is ever emitted at the barline column itself.
    // The step that *begins* the measure is therefore the first one at or after that column.
    const barlines = this.barlineColumns(parsedLines)
    let nextBarline = 0
    const startsMeasureAt = (column: number): boolean => {
      if (nextBarline >= barlines.length || column < barlines[nextBarline]) {
        return false
      }
      // Consume every barline this step has already passed, so a `||` repeat marks one step, not two.
      while (nextBarline < barlines.length && column >= barlines[nextBarline]) {
        nextBarline++
      }
      return true
    }

    // If there are no regular positions but content exists, create timing-only steps
    if (allPositions.length === 0) {
      // Check if we have content that should create timing steps
      const hasOnlyDashes = parsedLines.every(line => /^-+$/.test(line.originalContent))
      if (hasOnlyDashes && maxContentLength >= 6) {
        // For 6 dashes (------), create 3 timing steps
        const timingSteps = 3
        for (let i = 0; i < timingSteps; i++) {
          steps.push({
            fingering: [],
          })
        }
        return steps
      }
    }

    // For each position, collect all the fingerings from all strings
    for (let pos = 0; pos <= maxPosition; pos++) {
      const fingerings: FingeringWithArticulation[] = []

      for (const line of parsedLines) {
        const noteAtPosition = line.positions.find(p => p.position === pos)
        if (noteAtPosition) {
          if (noteAtPosition.fret !== null) {
            const fingering: FingeringWithArticulation = {
              str: line.stringNum,
              fret: noteAtPosition.fret,
            }
            
            // Add articulation to the fingering if present
            if (noteAtPosition.articulation) {
              fingering.articulation = noteAtPosition.articulation
            }

            if (noteAtPosition.ghost) {
              fingering.ghost = true
            }

            fingerings.push(fingering)
          } else if (options.includeMutedStrings) {
            // Include muted strings when option is enabled
            fingerings.push({
              str: line.stringNum,
              fret: null,
            })
          }
        }
      }

      // Check if this position is within a pause range
      const pauseRange = pauseRanges.find(range => pos >= range.start && pos < range.end)

      if (pauseRange && fingerings.length === 0) {
        // Only add pause on the first position of the range
        if (pos === pauseRange.start) {
          // A measure may open on a rest, so the first pause step of the range carries the mark.
          const startsMeasure = startsMeasureAt(pos)
          for (let i = 0; i < pauseRange.beats; i++) {
            steps.push({
              fingering: [],
              ...(i === 0 && startsMeasure ? { startsMeasure: true } : {}),
            })
            stepColumns.push(pos)
          }
        }
        // Skip other positions in the pause range
      } else if (fingerings.length > 0) {
        // A step carries one finish; the first string that declares one wins. Vibrato on a chord is
        // notated once in practice, so this does not lose information.
        const finish = parsedLines
          .map(line => line.positions.find(position => position.position === pos)?.finish)
          .find(Boolean)

        // Create a step with fingerings
        steps.push({
          fingering: fingerings,
          ...(finish ? { finish } : {}),
          ...(startsMeasureAt(pos) ? { startsMeasure: true } : {}),
        })
        stepColumns.push(pos)
      }
    }

    // Onsets are columns that actually carry a note; empty columns are rests and take no duration.
    const onsetSteps = steps.flatMap((step, index) =>
      (Array.isArray(step.fingering) ? step.fingering.length > 0 : Boolean(step.fingering)) ? [index] : []
    )
    /**
     * Coalesce adjacent barlines, exactly as `startsMeasureAt` does. A `||` repeat is one boundary;
     * passing both columns injects a 1-wide phantom measure that drags the confidence down and
     * silently costs a well-formed chart its rhythm.
     */
    const measureStarts = barlines.filter((column, index) => index === 0 || column - barlines[index - 1] > 1)
    const { durations } = inferRhythm({
      runStart: 0,
      // Column 0 opens a measure. `parseTabLine` strips the label's leading `|`, so the barline that
      // starts the run is never in `originalContent` — without it a two-bar run looks like one bar.
      measureStarts: measureStarts[0] === 0 ? measureStarts : [0, ...measureStarts],
      runEnd: maxPosition + 1,
      onsets: onsetSteps.map(index => stepColumns[index]),
      quarterNotesPerMeasure: options.quarterNotesPerMeasure,
    })
    // Empty when the run failed the confidence gate; the renderer then keeps its 'q' default.
    durations.forEach((duration, index) => {
      steps[onsetSteps[index]].duration = duration
    })

    return steps
  }

  /**
   * Columns where every string carries a `|`. Agreement across all strings is what separates a
   * measure boundary from a stray character, and it is cheap: a barline is a vertical thing.
   */
  private barlineColumns(parsedLines: ParsedTabLine[]): number[] {
    if (parsedLines.length === 0) {
      return []
    }
    const columnsPerLine = parsedLines.map(
      line => new Set([...line.originalContent].flatMap((char, index) => (char === '|' ? [index] : [])))
    )
    const [first, ...rest] = columnsPerLine
    return [...first].filter(column => rest.every(columns => columns.has(column))).sort((a, b) => a - b)
  }

  /**
   * Detect ranges where all strings have multiple consecutive dashes
   */
  private detectPauseRanges(parsedLines: ParsedTabLine[]): { start: number; end: number; beats: number }[] {
    if (parsedLines.length === 0) {
      return []
    }

    const maxLength = Math.max(...parsedLines.map(line => line.originalContent.length))
    const pauseRanges: { start: number; end: number; beats: number }[] = []

    for (let pos = 0; pos < maxLength; pos++) {
      // Check if all strings have consecutive dashes starting at this position
      let minConsecutiveDashes = Infinity
      let allStringsHaveDashes = true

      for (const line of parsedLines) {
        const dashCount = this.countConsecutiveDashesAtPosition(line.originalContent, pos)
        if (dashCount < 2) {
          // Require at least 2 consecutive dashes for a pause
          allStringsHaveDashes = false
          break
        }
        minConsecutiveDashes = Math.min(minConsecutiveDashes, dashCount)
      }

      if (allStringsHaveDashes && minConsecutiveDashes >= 2) {
        // Found a pause range
        pauseRanges.push({
          start: pos,
          end: pos + minConsecutiveDashes,
          beats: 1, // Create one pause step per dash sequence
        })
        // Skip ahead to avoid overlapping ranges
        pos += minConsecutiveDashes - 1
      }
    }

    return pauseRanges
  }

  /**
   * Count consecutive dashes at a specific position in the tab content
   */
  private countConsecutiveDashesAtPosition(content: string, position: number): number {
    if (position >= content.length || content[position] !== '-') {
      return 0
    }

    let count = 0
    for (let i = position; i < content.length && content[i] === '-'; i++) {
      count++
    }

    return count
  }


  /**
   * Map string letter to string number
   * For guitar: 1-6, where 1 is high E
   * For bass: 1-4, where 1 is high G
   */
  private getStringNumber(stringLetter: string): StringNumber {
    const stringNumber = this.stringIdentifiers[stringLetter]
    if (stringNumber === undefined) {
      throw new Error(`Unknown string identifier: ${stringLetter}`)
    }
    return stringNumber as StringNumber
  }
}

/**
 * Parse ASCII guitar tab text into Tab format
 */
export const parseAsciiTab = (asciiTab: string, options?: AsciiTabParseOptions): Tab => {
  const parser = new AsciiTabParser()
  return parser.parse(asciiTab, options)
}
